import { FC, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { stripPathLocale } from '../../../common/localeShared';

const ADITUDE_CPMSTAR_SKIN_ZONEFILE = '1222_54477_hb';
const ADITUDE_CPMSTAR_SKIN_DESKTOP_MEDIA_QUERY = '(min-width: 768px)';
const ADITUDE_CPMSTAR_SKIN_CLASS = 'uniqueCPMStarSiteskinClass';
const ADITUDE_CPMSTAR_SKIN_SCRIPT_SELECTOR = 'script[data-rio-aditude-cpmstar-skin="true"]';

export const ADITUDE_CPMSTAR_SKIN_BLOCKED_PATH_PREFIXES = [
  '/login',
  '/register',
  '/verify',
  '/reset',
  '/settings',
  '/tournaments',
  '/legacy-tournaments',
  '/community-events',
  '/events',
  '/event-teams',
  '/event-insights',
  '/event-hub',
  '/specs',
  '/stats',
  '/search'
] as const;

const matchesPathPrefix = (pathname: string, prefix: string) => {
  const strippedPathname = stripPathLocale(pathname || '/');

  return strippedPathname === prefix || strippedPathname.startsWith(`${prefix}/`);
};

export function isAditudeCpmstarSkinBlockedPath(pathname: string): boolean {
  return ADITUDE_CPMSTAR_SKIN_BLOCKED_PATH_PREFIXES.some((prefix) => matchesPathPrefix(pathname, prefix));
}

function isDesktopViewport() {
  return getDesktopMediaQueryList()?.matches ?? true;
}

function getDesktopMediaQueryList() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return null;
  }

  return window.matchMedia(ADITUDE_CPMSTAR_SKIN_DESKTOP_MEDIA_QUERY);
}

function useDesktopViewport() {
  const [isDesktop, setIsDesktop] = useState(isDesktopViewport);

  useEffect(() => {
    const mediaQueryList = getDesktopMediaQueryList();
    if (!mediaQueryList) {
      return;
    }

    const handleChange = () => setIsDesktop(mediaQueryList.matches);

    handleChange();

    if (typeof mediaQueryList.addEventListener === 'function') {
      mediaQueryList.addEventListener('change', handleChange);
      return () => mediaQueryList.removeEventListener('change', handleChange);
    }

    mediaQueryList.addListener(handleChange);
    return () => mediaQueryList.removeListener(handleChange);
  }, []);

  return isDesktop;
}

function getCpmstarZonefileHost() {
  if (window.location.hash === '#cpmstarDev') {
    return '//dev.server';
  }

  if (window.location.hash === '#cpmstarStaging') {
    return '//staging.server';
  }

  const proto = document.location.protocol;
  return proto === 'https:' || proto === 'file:' ? 'https://server' : '//cdn';
}

function cleanupAditudeCpmstarSkin() {
  document.querySelectorAll(`.${ADITUDE_CPMSTAR_SKIN_CLASS}`).forEach((element) => {
    if (element === document.body || element === document.documentElement) {
      element.classList.remove(ADITUDE_CPMSTAR_SKIN_CLASS);
      return;
    }

    element.remove();
  });
  document.querySelectorAll(ADITUDE_CPMSTAR_SKIN_SCRIPT_SELECTOR).forEach((element) => element.remove());
  failCpmstarAPI(ADITUDE_CPMSTAR_SKIN_ZONEFILE);
}

function failCpmstarAPI(zonefile: string) {
  const failQueuedCommand = (candidate: unknown) => {
    const fail = (candidate as { fail?: unknown } | null)?.fail;
    if (typeof fail === 'function') {
      fail();
    }
  };
  const win = window as any;
  const cpmstarQueue = win.cpmstarx;

  if (cpmstarQueue && Array.isArray(cpmstarQueue.cmd)) {
    cpmstarQueue.cmd.forEach(failQueuedCommand);
    cpmstarQueue.cmd.length = 0;
  }

  win.cpmstarAPI = win[`_${zonefile}`] = failQueuedCommand;
}

function appendCpmstarZonefileScript(zonefile: string) {
  const win = window as any;
  const cpmstarQueue: { cmd: unknown[] } = (win.cpmstarx = { cmd: [] });
  const rnd = Math.round(Math.random() * 999999);
  const script = document.createElement('script');

  script.type = 'text/javascript';
  script.async = true;
  script.onerror = () => failCpmstarAPI(zonefile);
  script.setAttribute('data-rio-aditude-cpmstar-skin', 'true');
  script.src = `${getCpmstarZonefileHost()}.cpmstar.com/cached/zonefiles/${zonefile}.js?rnd=${rnd}`;

  delete win[`_${zonefile}`];
  win.cpmstarAPI = (candidate: unknown) => {
    (cpmstarQueue.cmd = cpmstarQueue.cmd || []).push(candidate);
  };

  document.body.appendChild(script);
}

export const AditudeCpmstarSkin: FC = () => {
  const location = useLocation();
  const isDesktop = useDesktopViewport();

  useEffect(() => {
    cleanupAditudeCpmstarSkin();

    const shouldLoadSkin = Boolean(document.body) && !isAditudeCpmstarSkinBlockedPath(location.pathname) && isDesktop;
    if (shouldLoadSkin) {
      appendCpmstarZonefileScript(ADITUDE_CPMSTAR_SKIN_ZONEFILE);
    }

    return cleanupAditudeCpmstarSkin;
  }, [isDesktop, location.pathname]);

  return null;
};
